import "./DeleteModal.css";
import { Box } from "@mui/material";
import Title from "../title/Title";
import {
  PrimaryButton,
  SecondaryButton,
} from "../buttonComponent/ButtonComponent";
import ModalCloseButton from "./ModalCloseButton";
import StopIcon from "./img/agencySelector.svg";
import { formatCapitalize } from "../../config/utilities/formatCapitalize";
import Text from "../text/Text";
import ArrowRight from "./img/arrowRight.svg";
import RadioButton from "../radioButton/RadioButton";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 450,
  maxWidth: "90vw",
  bgcolor: "#FFFFFF",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  p: 0,
  boxShadow:
    "0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)",
  borderRadius: "12px",
};

const ConfirmTripModal = ({
  initialInput,
  input,
  setInput,
  loading,
  isOpen,
  onClose,
  onConfirm,
}) => {
  if (!isOpen) return null;


  function formatEta(eta) {
    // "19:16" -> "7:16 PM"
    if (!eta) return "N/A";
    const [h, m] = eta.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const hour = ((h + 11) % 12) + 1;
    return `${hour}:${m.toString().padStart(2, "0")} ${period}`;
  }

  function stopLabel(stop) {
    if (!stop) return "N/A";
    return `${stop.name} (${formatEta(stop.eta)})`;
  }

  function getChanges(initial, current, options = {}) {
    const changes = {};
    const skipKeys = options.skipKeys || ["updateScope"];

    for (const key in current) {
      if (skipKeys.includes(key)) continue;

      let initialVal = initial ? initial[key] : undefined;
      let currentVal = current[key];

      // Special handling for stopsArray
      if (key === "stopsArray") {
        const sortByEta = (arr) =>
          (arr || []).slice().sort((a, b) => a.eta.localeCompare(b.eta));

        initialVal = sortByEta(initialVal);
        currentVal = sortByEta(currentVal);

        const arrayChanges = [];
        const allIds = new Set([
          ...initialVal.map((s) => s.id),
          ...currentVal.map((s) => s.id),
        ]);

        for (const id of allIds) {
          const oldStop = initialVal.find((s) => s.id === id);
          const newStop = currentVal.find((s) => s.id === id);

          if (!oldStop && newStop) {
            arrayChanges.push({
              type: "added",
              to: stopLabel(newStop),
            });
          } else if (oldStop && !newStop) {
            arrayChanges.push({
              type: "removed",
              from: stopLabel(oldStop),
            });
          } else if (oldStop && newStop) {
            if (oldStop.name !== newStop.name || oldStop.eta !== newStop.eta) {
              arrayChanges.push({
                type: "modified",
                from: stopLabel(oldStop),
                to: stopLabel(newStop),
              });
            }
          }
        }

        if (arrayChanges.length > 0) {
          changes[key] = arrayChanges;
        }
      } else if (Array.isArray(currentVal)) {
        // fallback generic array handling
        if (JSON.stringify(initialVal) !== JSON.stringify(currentVal)) {
          changes[key] = { from: initialVal, to: currentVal };
        }
      } else if (typeof currentVal === "object" && currentVal !== null) {
        const nestedChanges = getChanges(initialVal || {}, currentVal, options);
        if (Object.keys(nestedChanges).length > 0) {
          changes[key] = nestedChanges;
        }
      } else if (currentVal !== initialVal) {
        if (key === "startTime") {
          changes[key] = {
            from: formatEta(initialVal),
            to: formatEta(currentVal),
          };
        } else {
          changes[key] = { from: initialVal, to: currentVal };
        }
      }
    }

    return changes;
  }

  const changes = getChanges(initialInput, input);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };
    setInput(updatedInput);
  };

  return (
    <div className="modal-backdrop">
      <Box sx={style}>
        <div>
          <div className="modal-header-content">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <img loading="lazy" src={StopIcon} alt="invite-users-icon" />
              <ModalCloseButton onClick={onClose} />
            </div>
            {Object.keys(changes).length > 0 && (
              <>
                <Title
                  title="Confirm Trip Changes"
                  deleteSubtitle
                  titleSize="1.125rem"
                  subtitleSize="0.875rem"
                  subtitle="By confirming, the changes below will be reflected for this trip:"
                  gap="0rem"
                  lineHeight="1.75rem"
                />

                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.75rem",
                  }}
                >
                  {Object.entries(changes).map(([field, diff]) => {
                    return (
                      <div
                        key={field}
                        style={{
                          border: "1px solid #E4E7EC",
                          borderRadius: "0.5rem",
                          padding: "0.75rem 1rem",
                          backgroundColor: "#F9FAFB",
                          minWidth: 400,
                          maxWidth: "80vw",
                        }}
                      >
                        {diff.from !== undefined && diff.to !== undefined ? (
                          // Standard field diffs
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.5rem",
                            }}
                          >
                            <Text
                              size="0.9rem"
                              color="#101828"
                              weight="600"
                              text={
                                field === "tripName"
                                  ? "Trip Name:"
                                  : field === "startTime"
                                  ? "Start Time:"
                                  : `${formatCapitalize(field)}:`
                              }
                            />
                            <Text
                              size="0.875rem"
                              color="#B42318"
                              weight="500"
                              text={String(diff.from) || "N/A"}
                            />
                            <img
                              src={ArrowRight}
                              alt="arrow right"
                              style={{ width: 16, height: 16 }}
                            />
                            <Text
                              size="0.875rem"
                              color="#027A48"
                              weight="500"
                              text={String(diff.to) || "N/A"}
                            />
                          </div>
                        ) : (
                          // Special handling for stopsArray
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: "0.5rem",
                            }}
                          >
                            <Text
                              size="0.9rem"
                              color="#101828"
                              weight="600"
                              text="Stop Changes:"
                            />

                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "0.35rem",
                              }}
                            >
                              {diff.map((stopChange, idx) => (
                                <div
                                  key={`stop-${idx}`}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                  }}
                                >
                                  {stopChange.type === "added" && (
                                    <Text
                                      size="0.875rem"
                                      color="#027A48"
                                      weight="500"
                                      text={`Added ${stopChange.to}`}
                                    />
                                  )}

                                  {stopChange.type === "removed" && (
                                    <Text
                                      size="0.875rem"
                                      color="#B42318"
                                      weight="500"
                                      text={`Removed ${stopChange.from}`}
                                    />
                                  )}

                                  {stopChange.type === "modified" && (
                                    <>
                                      <Text
                                        size="0.875rem"
                                        color="#B42318"
                                        weight="500"
                                        text={stopChange.from}
                                      />
                                      <img
                                        src={ArrowRight}
                                        alt="arrow right"
                                        style={{ width: 16, height: 16 }}
                                      />
                                      <Text
                                        size="0.875rem"
                                        color="#027A48"
                                        weight="500"
                                        text={stopChange.to}
                                      />
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            <>
              <Title
                title="Schedule Updates"
                deleteSubtitle
                titleSize="1.125rem"
                subtitleSize="0.875rem"
                gap="0rem"
                lineHeight="1.75rem"
              >
                How would you like these changes to affect schedules?
              </Title>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.35rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1rem",
                  }}
                >
                  <RadioButton
                    name="updateScope"
                    value="none"
                    text="None"
                    onChange={(e) => handleInputChange(e)}
                    checked={input.updateScope === "none"}
                  />
                  <RadioButton
                    name="updateScope"
                    value="today"
                    text="Today"
                    onChange={(e) => handleInputChange(e)}
                    checked={input.updateScope === "today"}
                  />
                  <RadioButton
                    name="updateScope"
                    value="future"
                    text="Future"
                    onChange={(e) => handleInputChange(e)}
                    checked={input.updateScope === "future"}
                  />
                  <RadioButton
                    name="updateScope"
                    value="both"
                    text="Both"
                    onChange={(e) => handleInputChange(e)}
                    checked={input.updateScope === "both"}
                  />
                </div>
                <Text
                  size="0.75rem"
                  color="#667085"
                  weight="400"
                  text="“Both” syncs today’s schedule and all future schedules."
                />
              </div>
            </>
          </div>
          <div className="button-container-delete">
            <SecondaryButton
              label="Cancel"
              width="190px"
              imageIcon={false}
              onClickFunction={onClose}
            />
            <PrimaryButton
              loading={loading}
              label="Confirm"
              width="190px"
              disable={false}
              onClickFunction={onConfirm}
            />
          </div>
        </div>
      </Box>
    </div>
  );
};

export default ConfirmTripModal;
